// 预留：后期接入后端时可使用
// const API_BASE = process.env.VUE_APP_API_BASE_URL || '';
// function assertOk(res) {
//   if (!res.ok) {
//     const err = new Error(`Request failed: ${res.status}`);
//     err.status = res.status;
//     throw err;
//   }
//   return res;
// }

/**
 * 该文件是“可替换”的后端适配层：
 * - 上传相关能力抽到 qiniuUploader，方便在别的页面复用
 * - 如需切换到其他存储，只需要改动 qiniuUploader
 */

import { uploadToQiniu } from './qiniuUploader';
import { promptLogin } from '../utils/auth';
import { isTiffLikeFile, tiffFileToJpegFile } from '../utils/tiffToJpeg';

async function parseApiJson(res) {
  if (res.status === 401) {
    promptLogin();
    throw new Error('未登录或登录已失效');
  }
  const json = await res.json();
  if (json.code === 401) {
    promptLogin();
    throw new Error(json.msg || '未登录或登录已失效');
  }
  if (json.code !== 200) {
    throw new Error(json.msg || 'request failed');
  }
  return json;
}

export async function uploadSemImage(file) {
  let uploadFile = file;
  if (isTiffLikeFile(file)) {
    uploadFile = await tiffFileToJpegFile(file);
  }
  const { url, key, raw } = await uploadToQiniu(uploadFile);
  const previewUrl = url || URL.createObjectURL(uploadFile);

  return {
    taskId: `task_${Date.now()}`,
    previewUrl,
    qiniu: {
      key,
      raw,
    },
  };
}

export async function createSemTask({
  taskId,
  clientId = '',
  imgUrl = '',
  materialStructure = '',
  materialComposition = '',
  rulerInfo = null,
}) {
  const fd = new FormData();
  fd.append('task_id', String(taskId || `task_${Date.now()}`));
  fd.append('client_id', String(clientId || 'anonymous'));
  fd.append('img_url', String(imgUrl || ''));
  if (materialStructure) fd.append('material_structure', String(materialStructure));
  if (materialComposition) fd.append('material_composition', String(materialComposition));
  if (rulerInfo) fd.append('ruler_info', JSON.stringify(rulerInfo));
  const res = await fetch('/api/sem/task/create', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const json = await parseApiJson(res);
  return json.data;
}

export async function getSemTask(taskId) {
  const url = `/api/sem/task/get?task_id=${encodeURIComponent(String(taskId || ''))}`;
  const res = await fetch(url, { method: 'GET', credentials: 'include' });
  const json = await parseApiJson(res);
  return json.data;
}

export async function getSemTaskSteps(taskId) {
  const url = `/api/sem/task/steps?task_id=${encodeURIComponent(String(taskId || ''))}`;
  const res = await fetch(url, { method: 'GET', credentials: 'include' });
  const json = await parseApiJson(res);
  return Array.isArray(json.data) ? json.data : [];
}

/**
 * @param {number} limit
 * @param {{ segmentStatus?: 'all' | 'filled' | 'unfilled', hasSegmentSuccess?: boolean }} [options]
 *   segmentStatus: all=全部上传任务（第一步）；filled=status=success 已填色（第二步）；unfilled=无成功 segment 步骤
 *   hasSegmentSuccess: 兼容旧参数，等价于 segmentStatus=filled
 */
export async function getSemTaskHistory(limit = 50, options = {}) {
  const qs = new URLSearchParams();
  qs.set('limit', String(limit || 50));
  let seg = options.segmentStatus;
  if (options.hasSegmentSuccess) seg = 'filled';
  if (seg === 'filled' || seg === 'unfilled') {
    qs.set('segment_status', seg);
  }
  const url = `/api/sem/task/list?${qs.toString()}`;
  const res = await fetch(url, { method: 'GET', credentials: 'include' });
  const json = await parseApiJson(res);
  return Array.isArray(json.data) ? json.data : [];
}

export async function detectScale({ taskId, scaleValue, scaleUnit }) {
  // mock: 标尺识别结果
  return {
    taskId,
    scale: {
      value: Number(scaleValue || 100),
      unit: scaleUnit || 'μm',
    },
  };
}

async function fetchImageAsBlob(imageUrl) {
  const r = await fetch(imageUrl, { mode: 'cors' });
  if (!r.ok) {
    throw new Error(`Failed to fetch image: ${r.status}`);
  }
  return r.blob();
}

export async function runSegmentation({
  taskId,
  previewUrl,
  minAreaPx = null,
  ratio = null,
  unit = 'px',
  clientId = 'anonymous',
  materialStructure = '',
  materialComposition = '',
  rulerInfo = null,
}) {
  /**
   * 调用 Python 填色/分割服务：
   * POST /api/sem/segment
   * - image: form-data 文件
   * - min_area_px: 数字（可选）
   *
   * Python 返回：
   * data.overlayUrl: /static/overlay_xxx.png
   */
  if (!previewUrl) {
    return { taskId, overlayUrl: null, labelUrl: null, stats: null };
  }

  const imageBlob = await fetchImageAsBlob(previewUrl);
  const fd = new FormData();
  fd.append('image', imageBlob, 'sem.png');
  if (minAreaPx != null && Number.isFinite(Number(minAreaPx))) {
    fd.append('min_area_px', String(minAreaPx));
  }
  if (taskId) fd.append('task_id', String(taskId));
  if (clientId) fd.append('client_id', String(clientId));
  if (previewUrl) fd.append('img_url', String(previewUrl));
  if (materialStructure) fd.append('material_structure', String(materialStructure));
  if (materialComposition) fd.append('material_composition', String(materialComposition));
  if (rulerInfo) fd.append('ruler_info', JSON.stringify(rulerInfo));
  if (ratio != null && Number.isFinite(Number(ratio))) {
    fd.append('ratio', String(ratio));
    fd.append('unit', unit);
  }

  const res = await fetch('/api/sem/segment', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const json = await parseApiJson(res);

  const overlayRel = json.data?.overlayUrl || null;
  const labelRel = json.data?.labelUrl || null;
  // overlayRel/labelRel 为 Nginx 反代后的相对路径：/static/xxx.png
  const overlayUrl = overlayRel;
  const labelUrl = labelRel;
  const stats = json.data?.stats || null;

  return { taskId, overlayUrl, labelUrl, stats };
}

export async function supplementSegmentationByPoint({
  previewUrl,
  pointX,
  pointY,
  ratio = null,
  unit = 'px',
  minAreaPx = 0,
}) {
  if (!previewUrl) return null;
  const imageBlob = await fetchImageAsBlob(previewUrl);
  const fd = new FormData();
  fd.append('image', imageBlob, 'sem.png');
  fd.append('point_x', String(pointX));
  fd.append('point_y', String(pointY));
  if (ratio != null && Number.isFinite(Number(ratio))) {
    fd.append('ratio', String(ratio));
    fd.append('unit', String(unit || 'px'));
  }
  if (minAreaPx != null && Number.isFinite(Number(minAreaPx))) {
    fd.append('min_area_px', String(minAreaPx));
  }
  const res = await fetch('/api/sem/segment/point', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const json = await parseApiJson(res);
  return json.data || null;
}

export async function computeStats({ taskId }) {
  // mock: 粒径统计
  return {
    taskId,
    summary: {
      count: 116,
      mean: 5.83,
      std: 2.41,
      p10: 2.9,
      p50: 6.1,
      p90: 10.7,
      unit: 'μm',
    },
    histogram: Array.from({ length: 10 }).map((_, i) => ({
      bin: i + 1,
      value: Math.round(5 + Math.random() * 18),
    })),
  };
}

function dataUrlToBlob(dataUrl) {
  const arr = String(dataUrl || '').split(',');
  if (arr.length < 2) throw new Error('invalid data url');
  const mimeMatch = arr[0].match(/data:(.*?);base64/);
  const mime = (mimeMatch && mimeMatch[1]) || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new Blob([u8arr], { type: mime });
}

export async function saveStep2EditedResult({
  taskId,
  overlayUrl,
  editedOverlayDataUrl,
  editedStats = null,
  measurements = null,
}) {
  if (!taskId) return null;
  if (!editedOverlayDataUrl) return null;

  const editedBlob = dataUrlToBlob(editedOverlayDataUrl);
  const uploaded = await uploadToQiniu(editedBlob);
  const editedUrl = uploaded?.url || '';

  const fd = new FormData();
  fd.append('task_id', String(taskId));
  fd.append('status', 'success');
  fd.append('overlay_url', String(overlayUrl || ''));
  fd.append('label_url', String(editedUrl || ''));
  if (editedStats) fd.append('edited_stats', JSON.stringify(editedStats));
  if (measurements) fd.append('measurements', JSON.stringify(measurements));
  const res = await fetch('/api/sem/task/update', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  await parseApiJson(res);
  return { editedUrl };
}

